// Modal para actualizar un detalle de inventario
import React, { useState, useEffect } from 'react';
import { Alert, Button, Modal, Spinner } from '../UI/Index';
import inventoryService from '../../services/inventoryService';

const UpdateDetailModal = ({ isOpen, onClose, detail, onDetailUpdated, user }) => {
  const [quantity, setQuantity] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [productSPUPhoto, setProductSPUPhoto] = useState(null);
  const [polarikStock, setPolarikStock] = useState(null);
  const [loadingStock, setLoadingStock] = useState(false);
  const [stockError, setStockError] = useState('');

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);



  useEffect(() => {
    if (!isOpen || !detail) return;

    const fetchPhoto = async () => {
      try {
        const data = await inventoryService.checkProduct(detail.product_code);
        setProductSPUPhoto(data.image ?? null);
      } catch (err) {
        console.error('Error cargando la foto del producto:', err);
        setProductSPUPhoto('');
      }
    };

    fetchPhoto();
  }, [isOpen, detail]);

  useEffect(() => {

    const loadPolarikStock = async () => {
      if (!isOpen || !detail || !shouldShowStock()) {
        return;
      }

      try {
        setLoadingStock(true);
        setStockError('');

        const response = await inventoryService.getStock({
          inventoryDetailId: detail.inventory_detail_id
        });

        if (response.success) {
          setPolarikStock(response.stockQty);
        } else {
          setStockError('No se pudo obtener el stock actual');
        }
      } catch (err) {
        setStockError('Error al consultar stock actual');
        console.error('Error obteniendo stock:', err);
      } finally {
        setLoadingStock(false);
      }
    };

    if (isOpen) {
      loadPolarikStock();
    } else {
      setPolarikStock(null);
      setStockError('');
      setLoadingStock(false);
    }
  }, [isOpen, detail, user]);

const handleQuantityChange = (e) => {
  let value = e.target.value;

  // Solo permitir números
  if (value === '' || /^\d+$/.test(value)) {
    // Limitar a máximo 3 caracteres
    if (value.length > 4) {
      value = value.slice(0, 4);
    }
    setQuantity(value);
  }
};

  const processUpdateWithForce = async (quantityToUpdate) => {
    setError('');
    setSuccess('');

    try {
      setLoading(true);

      const updateData = user?.profileId === 3 && detail.requires_validation === 1
        ? {
          detailId: detail.inventory_detail_id,
          qtyPickedValidation: quantityToUpdate,
          forceUpdate: true
        }
        : {
          detailId: detail.inventory_detail_id,
          qtyInputPicked: quantityToUpdate,
          forceUpdate: true
        };

      const response = await inventoryService.updateInventoryDetail(
        detail.inventory_detail_id,
        updateData
      );

      if (response.success) {
        setSuccess('Unidades actualizadas correctamente');

        setTimeout(() => {
          onDetailUpdated();
        }, 1500);
      } else {
        setError(response.message || 'Error al actualizar el detalle');
      }
    } catch (err) {
      setError(err.message || 'Error al actualizar el detalle');
    } finally {
      setLoading(false);
    }
  };


 const handleSubmit = async (e) => {
  e.preventDefault();
  setError('');
  setSuccess('');

  if (!quantity) {
    setError('Debes ingresar una cantidad');
    return;
  }

  const numQuantity = parseInt(quantity, 10);
  if (isNaN(numQuantity) || numQuantity < 0) {
    setError('La cantidad debe ser un número positivo');
    return;
  }

  if (numQuantity > 4000) {
    setError('La cantidad máxima permitida es 4000 unidades');
    return;
  }

  try {
    setLoading(true);

    const updateData = user?.profileId === 3 && detail.requires_validation === 1
      ? {
        detailId: detail.inventory_detail_id,
        qtyPickedValidation: numQuantity
      }
      : {
        detailId: detail.inventory_detail_id,
        qtyInputPicked: numQuantity
      };

    const response = await inventoryService.updateInventoryDetail(
      detail.inventory_detail_id,
      updateData
    );

    if (!response.success && response.show_modal_validation) {
      setConfirmationData({
        quantity: numQuantity,
        message: response.message,
        currentStock: response.currentStock,
        userQuantity: response.userQuantity,
        productCost: response.productCost
      });
      setShowConfirmation(true);
      setLoading(false);
      return;
    }

    if (response.success) {
      setSuccess('Unidades actualizadas correctamente');

      setTimeout(() => {
        onDetailUpdated();
      }, 1500);
    } else {
      setError(response.message || 'Error al actualizar el detalle');
    }
  } catch (err) {
    setError(err.message || 'Error al actualizar el detalle');
  } finally {
    setLoading(false);
  }
};

  const handleConfirmQuantity = async () => {
    setShowConfirmation(false);
    await processUpdateWithForce(confirmationData.quantity);
    setConfirmationData(null);
  };

  const handleCancelCorrection = () => {
    setShowConfirmation(false);
    setConfirmationData(null);
  };

  const handleMainModalClose = () => {
    if (!showConfirmation) {
      onClose();
    }
  };

  const shouldShowStock = () => {
    if (user?.isAdmin === 1) {
      return true;
    }

    if (user?.profileId === 3 && detail?.requires_validation === 1 && detail?.is_sensitive === 0) {
      return true;
    }

    return false;
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleMainModalClose}
        title="Confirmar Cantidad"
        size="md"
      >
        {error && <Alert type="error" message={error} onClose={() => setError('')} />}
        {success && <Alert type="success" message={success} />}

        <div className="mt-4 mb-6">
          <div className="p-4 mb-4 border border-gray-200 rounded-md">
            <h3 className="mb-2 text-lg font-medium">Información del Producto</h3>
            <div className="flex justify-center my-3">
              {productSPUPhoto === null ? (
                <b className="my-auto">Cargando foto...</b>
              ) : productSPUPhoto ? (
                <img
                  src={productSPUPhoto}
                  alt={detail.product_name}
                  className="w-32 h-32 object-cover rounded-md border border-gray-300 bg-white"
                />
              ) : (
                <b className="my-auto">El producto no tiene imagen asociada</b>
              )}
            </div>

            <p className="mb-1 text-sm text-gray-700">
              <span className="font-medium">Nombre producto:</span> {detail.product_name}
            </p>
            <p className="mb-1 text-sm text-gray-700">
              <span className="font-medium">Product_Code:</span> {detail.product_code}
            </p>

            {detail.qty_input_picked !== null && (
              <p className="mb-1 text-sm text-gray-700">
                <span className="font-medium">Cantidad picada:</span> {detail.qty_input_picked}
              </p>
            )}

            {shouldShowStock() && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm font-medium text-blue-800 mb-1">
                  Stock actual en Polarik:
                </p>
                {loadingStock ? (
                  <div className="flex items-center">
                    <Spinner size="sm" className="mr-2" />
                    <span className="text-sm text-blue-600">Consultando...</span>
                  </div>
                ) : stockError ? (
                  <span className="text-sm text-red-600">{stockError}</span>
                ) : polarikStock !== null ? (
                  <span className="text-lg font-bold text-blue-900">{polarikStock} unidades</span>
                ) : (
                  <span className="text-sm text-gray-500">No disponible</span>
                )}
              </div>
            )}

            {detail.requires_validation === 1 && (
              <p className="mt-2 text-sm text-amber-600 font-medium">
                Este producto requiere validación por un manager.
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">
                {user?.profileId === 3 && detail.requires_validation === 1
                  ? 'Cantidad a validar *'
                  : 'Nueva cantidad *'}
              </label>
              <input
                type="text"
                id="quantity"
                value={quantity}
                onChange={handleQuantityChange}
                required
                maxLength={4}
                className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Introduce la cantidad"
                autoFocus
              />
            </div>

            <div className="flex justify-end space-x-3">
              <Button type="secondary" onClick={onClose} disabled={loading}>
                Cancelar
              </Button>
              <Button type="primary" buttonType="submit" disabled={loading || success !== ''}>
                {loading ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Actualizando...
                  </>
                ) : (
                  'Guardar'
                )}
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      <Modal
        isOpen={showConfirmation}
        onClose={() => { }}
        title="Diferencia Importante Detectada"
        size="md"
      >
        <div className="mt-4 mb-6">
          <div className="p-4 mb-4 border border-amber-200 bg-amber-50 rounded-md">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-amber-800">
                  Diferencia importante detectada
                </h3>
                <div className="mt-2 text-sm text-amber-700">
                  <p>{confirmationData?.message}</p>
                  <p className="mt-1">
                    <strong>¿Estás seguro que deseas introducir esta cantidad?</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              type="secondary"
              onClick={handleCancelCorrection}
              disabled={loading}
            >
              Cancelar y corregir
            </Button>
            <Button
              type="primary"
              onClick={handleConfirmQuantity}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Actualizando...
                </>
              ) : (
                'Confirmar cantidad'
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default UpdateDetailModal;